import React from 'react';
import ExportButton from '@/components/common/ExportButton';
import ShowForViews from '@/components/common/ShowForViews';
import IntegrationID from '@/constants/integrations/IntegrationID';
import MailchimpTiers from '@/constants/integrations/MailchimpTiers';
import UserViewEnums from '@/constants/users/UserViewEnums';
import useExternalIntegrations from '@/lib/hooks/swr/useExternalIntegrations';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import { useQueryProvider } from '@/modules/QueryProvider';
import type { ExternalIntegration } from '@/types/Integration';
import ConstituentExportCSVDialog from './ConstituentExportCSVDialog';

type ConstituentExportProps = {
  className?: string;
  totalConstituents: number;
};
function ConstituentExport({
  className = '',
  totalConstituents,
}: ConstituentExportProps) {
  const { query } = useQueryProvider();
  const { openDialog } = useDialogManager();

  const [isExportCSVModalActive, setIsExportCSVModalActive] =
    React.useState(false);
  const [isExportDestinationModalActive, setIsExportDestinationModalActive] =
    React.useState(false);
  const [destination, setDestination] = React.useState('');
  const [mailchimpPricingTier, setMailchimpPricingTier] = React.useState(
    MailchimpTiers.FREE,
  );

  const {
    data,
  }: { data: { externalIntegrations: ExternalIntegration[] } | undefined } =
    useExternalIntegrations();
  const { externalIntegrations } = data || {};
  React.useEffect(() => {
    const mailchimpIntegration = externalIntegrations?.find(
      (int) => int.source === IntegrationID.MAILCHIMP,
    );
    const pricingTier =
      mailchimpIntegration?.pricingTier || MailchimpTiers.FREE;

    setMailchimpPricingTier(pricingTier);
  }, [externalIntegrations]);

  const openConstituentExportChoiceDialog = React.useCallback(() => {
    openDialog(Dialogs.CONSTITUENT_EXPORT_CHOICE, {
      setDestination,
      setIsExportCSVModalActive,
      setIsExportDestinationModalActive,
      setMailchimpPricingTier,
    });
  }, [
    setDestination,
    setIsExportCSVModalActive,
    setIsExportDestinationModalActive,
    setMailchimpPricingTier,
  ]);

  React.useEffect(() => {
    if (isExportDestinationModalActive) {
      openDialog(Dialogs.CONSTITUENT_EXPORT_DESTINATION, {
        destination,
        setDestination,
        filters: query,
        onClose: () => {
          setIsExportDestinationModalActive(false);
        },
        totalConstituents,
        mailchimpPricingTier,
      });
    }
  }, [
    destination,
    isExportDestinationModalActive,
    setDestination,
    query,
    totalConstituents,
    mailchimpPricingTier,
  ]);

  return (
    <>
      <ShowForViews showFor={[UserViewEnums.ADMIN, UserViewEnums.EDITOR]}>
        <ExportButton
          className={className}
          data-testid="constituent-export-button"
          type="button"
          variant="contained"
          color="primary"
          size="small"
          onClick={openConstituentExportChoiceDialog}
        />
      </ShowForViews>
      <ConstituentExportCSVDialog
        filters={query}
        onClose={() => setIsExportCSVModalActive(false)}
        open={isExportCSVModalActive}
        totalConstituents={totalConstituents}
      />
    </>
  );
}

export default ConstituentExport;
